import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** `meeting-recording-links.get` (packages/api/src/services/read-ai/meeting-recordings/meeting-recording-links.ts). */
export interface RecordingLink {
  meetingId: number;
  state: "stored" | "attached" | "none";
  url: string | null;
  expiresAt: string | null;
  fileName: string | null;
  contentType: string | null;
  playable: boolean;
  detail: string;
}

interface RawService {
  get: (id: number, params?: object) => Promise<unknown>;
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;
const codeOf = (err: unknown): number | null => {
  const c = (err as { code?: unknown } | null)?.code;
  return typeof c === "number" ? c : null;
};

/**
 * Meeting Detail → the Recording card (#2614): play or download the meeting's
 * recording from HQ.
 *
 * THE LINK IS A CREDENTIAL. `meeting-recording-links` answers a signed URL that
 * works for about an hour, so it is fetched through the RAW Feathers client
 * (a ServiceStore would cache it with the entities) only when the person asks
 * for the recording, held here and nowhere else, re-minted when it has
 * expired, and dropped on route exit and on sign-out (`reset`).
 */
class MeetingRecordingStore {
  rootStore: RootStore;
  meetingId: number | null = null;
  link: RecordingLink | null = null;
  loading = false;
  error: string | null = null;
  forbidden = false;
  /** The person asked to watch it here. */
  playing = false;
  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get raw(): RawService | null {
    const app = (
      this.rootStore as unknown as {
        app?: { service: (p: string) => RawService };
      }
    ).app;
    return app?.service("meeting-recording-links") ?? null;
  }

  /** The link in hand has passed its expiry (or carries none). */
  linkExpired(now: number = Date.now()): boolean {
    const at = this.link?.expiresAt ? Date.parse(this.link.expiresAt) : NaN;
    // A minute's margin: a link about to lapse fails on the player's next range request.
    return !Number.isFinite(at) || at - 60_000 <= now;
  }

  setMeetingId(id: number | string | null) {
    const n = Number(id);
    const next = Number.isInteger(n) && n > 0 ? n : null;
    if (next === this.meetingId) return;
    this.dispose();
    this.meetingId = next;
  }

  /** Mint a link now. Resolves the link, or null when it could not be had (and `error` says why). */
  async fetchLink(): Promise<RecordingLink | null> {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.seq;
    const id = this.meetingId;
    const raw = this.raw;
    if (id == null || !raw) return null;
    this.loading = true;
    this.error = null;
    try {
      const link = (await raw.get(id)) as RecordingLink;
      // Another meeting opened meanwhile (dispose bumps `seq`): this link is not its.
      if (ended() || seq !== this.seq || id !== this.meetingId) return null;
      runInAction(() => {
        this.link = link;
        this.forbidden = false;
      });
      return link;
    } catch (err) {
      if (isSessionEnded(err) || ended() || seq !== this.seq) return null;
      runInAction(() => {
        this.forbidden = codeOf(err) === 403;
        this.error = messageOf(err, "HQ could not open this recording.");
      });
      return null;
    } finally {
      if (!ended() && seq === this.seq) runInAction(() => (this.loading = false));
    }
  }

  /** "Open recording": a fresh link when the one in hand has lapsed, then the player. */
  async open() {
    if (!this.link || this.linkExpired()) await this.fetchLink();
    runInAction(() => {
      if (this.link?.url && this.link.state === "stored") this.playing = true;
    });
  }

  /** The player failed: when the link has lapsed, mint a new one (the card resumes where it was). */
  async refreshIfExpired(): Promise<boolean> {
    if (!this.linkExpired()) return false;
    return Boolean(await this.fetchLink());
  }

  reset() {
    this.dispose();
    this.seq = 0;
  }

  dispose() {
    // Anything still in flight belongs to the meeting being left.
    this.seq += 1;
    this.meetingId = null;
    this.link = null;
    this.loading = false;
    this.error = null;
    this.forbidden = false;
    this.playing = false;
  }
}

export default MeetingRecordingStore;
